// Window-level error catcher.
//
// React's `AppErrorBoundary` only sees errors thrown during render — async
// failures from background polling, event handlers and unhandled promise
// rejections bypass it entirely and would otherwise: (a) show CRA's red
// runtime-error overlay in dev, or (b) silently fail in production.
//
// This component installs `unhandledrejection` + `error` listeners on
// mount and:
//   1. Surfaces a single sonner toast so the pilot knows something
//      hiccuped (don't blame their network when it's our bug).
//   2. Pipes the error into the existing `/api/_client-error` telemetry
//      feed for monitoring/alerts.
//   3. De-dupes by error message hash on a 30s rolling window so a flood
//      of repeats doesn't spam toasts.
import { useEffect } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const DEDUPE_WINDOW_MS = 30_000;
const _seenAt = new Map(); // message → last-reported-at timestamp

function shouldReport(message) {
  const now = Date.now();
  const last = _seenAt.get(message) || 0;
  if (now - last < DEDUPE_WINDOW_MS) return false;
  _seenAt.set(message, now);
  // Light prune to keep the map small (>50 entries → drop oldest).
  if (_seenAt.size > 50) {
    const oldest = [..._seenAt.entries()].sort((a, b) => a[1] - b[1])[0];
    if (oldest) _seenAt.delete(oldest[0]);
  }
  return true;
}

function reportTelemetry(payload) {
  // Fire-and-forget — server already dedupes too.
  try {
    api.post("/_client-error", payload).catch(() => {});
  } catch { /* swallow */ }
}

export default function GlobalErrorHandler() {
  useEffect(() => {
    const onRejection = (e) => {
      const reason = e?.reason;
      // Don't double-report Axios errors from API calls — the page-level
      // try/catch handlers already surface those with their own toasts.
      if (reason?.isAxiosError) return;
      const message = String(reason?.message || reason || "Unknown rejection").slice(0, 500);
      if (!shouldReport(message)) return;
      toast.error("Something hiccuped", {
        description: message.length > 80 ? `${message.slice(0, 80)}…` : message,
        duration: 4000,
      });
      reportTelemetry({
        message,
        stack: String(reason?.stack || "").slice(0, 4000),
        surface: "unhandled-rejection",
        url: typeof window !== "undefined" ? window.location.pathname : "",
        user_agent: typeof navigator !== "undefined" ? (navigator.userAgent || "").slice(0, 200) : "",
      });
    };

    const onError = (e) => {
      const message = String(e?.message || e?.error?.message || "Unknown error").slice(0, 500);
      // Browser extension noise (translate, ad-blockers etc.) often leaks
      // here as `Script error.` — telemetry already filters it server-side
      // but suppress the toast too so pilots aren't bothered.
      // ResizeObserver "loop" warnings are harmless browser layout noise
      // (Chromium fires them whenever a observer callback triggers another
      // layout). They're not actionable bugs so we drop both phrasings.
      if (
        message === "Script error."
        || message.startsWith("ResizeObserver loop")
      ) return;
      if (!shouldReport(message)) return;
      toast.error("Something hiccuped", {
        description: message.length > 80 ? `${message.slice(0, 80)}…` : message,
        duration: 4000,
      });
      reportTelemetry({
        message,
        stack: String(e?.error?.stack || "").slice(0, 4000),
        surface: "window-error",
        url: typeof window !== "undefined" ? window.location.pathname : "",
        user_agent: typeof navigator !== "undefined" ? (navigator.userAgent || "").slice(0, 200) : "",
      });
    };

    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("error", onError);
    return () => {
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("error", onError);
    };
  }, []);

  return null;
}
